import { useCallback, useEffect } from 'react';
import { useProductEngine } from './ProductEngine';
import { GridLayout } from '../../components/GridLayout';
import { AttributeGroup } from '../attribute/AttributeGroup';
import { SolutionSelector } from '../../components/SolutionSelector';
import { RoleSelector } from '../../components/RoleSelector';
import '../../styles/engine.css';

export function ProductForm({ solutionId: propSolutionId, onSubmit }) {
  const eng = useProductEngine();
  const {
    config, visibility, layout, solutionId, role,
    values, errors, isDirty, isSubmitting, lastSaved,
    handleFieldChange, handleSubmit, handleSaveDraft,
    setSolution, setRole, isLoading
  } = eng;

  useEffect(() => {
    if (propSolutionId && propSolutionId !== solutionId) setSolution(propSolutionId);
  }, [propSolutionId, solutionId, setSolution]);

  const renderGroup = useCallback((p) => {
    if (!config || !visibility) return null;
    const group = config.groups.get(p.groupId);
    if (!group) return null;
    const attrs = group.attributeIds.map(id => config.attributes.get(id)).filter(Boolean);
    return (
      <AttributeGroup
        key={group.id} groupId={group.id} label={group.label} icon={group.icon}
        attributes={attrs}
        visibleAttributeIds={visibility.visibleAttributeIds}
        editableAttributeIds={visibility.editableAttributeIds}
        values={values} errors={errors}
        requiredOverrides={visibility.computedState.requiredOverrides}
        unrequiredOverrides={visibility.computedState.unrequiredOverrides}
        onFieldChange={handleFieldChange}
        collapsible={group.collapsible} defaultCollapsed={group.defaultCollapsed}
      />
    );
  }, [config, visibility, values, errors, handleFieldChange]);

  const errCount = Object.keys(errors).length;

  return (
    <div className="fe">
      <div className="fe-bar">
        <div className="fe-status">
          {isDirty && <span className="badge badge-warn">Unsaved</span>}
          {lastSaved && <span className="badge badge-ok">Saved {new Date(lastSaved).toLocaleTimeString()}</span>}
          {errCount > 0 && <span className="badge badge-err">{errCount} error{errCount !== 1 ? 's' : ''}</span>}
        </div>
        <div className="fe-controls">
          <SolutionSelector currentSolutionId={solutionId} isDirty={isDirty} onSelect={setSolution} />
          <RoleSelector currentRole={role} onSelect={setRole} />
        </div>
      </div>

      {config && (
        <div className="fe-head">
          <h1 className="fe-title">{config.solution.icon} {config.solution.label}</h1>
          {config.solution.description && <p className="fe-desc">{config.solution.description}</p>}
        </div>
      )}

      {errCount > 0 && (
        <div className="err-sum" role="alert">
          <strong>Please fix {errCount} error{errCount !== 1 ? 's' : ''} before submitting:</strong>
          <ul>
            {Object.entries(errors).slice(0, 6).map(([fid, msg]) => (
              <li key={fid}><a href={`#field-${fid}`}>{config?.attributes.get(fid)?.label ?? fid}: {msg}</a></li>
            ))}
            {errCount > 6 && <li>...and {errCount - 6} more</li>}
          </ul>
        </div>
      )}

      <form className="fe-form" onSubmit={e => { e.preventDefault(); handleSubmit(onSubmit ?? (() => Promise.resolve())); }} noValidate>
        {isLoading
          ? <div className="fe-load">Loading configuration...</div>
          : config && layout && visibility
            ? <GridLayout layout={layout} visibleGroupIds={visibility.visibleGroupIds} renderGroup={renderGroup} />
            : <div className="fe-load">No configuration found.</div>
        }
        <div className="fe-actions">
          <button type="button" className="btn btn-sec" onClick={handleSaveDraft} disabled={!isDirty || isSubmitting}>Save Draft</button>
          <button type="submit" className="btn btn-pri" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Product'}
          </button>
        </div>
      </form>
    </div>
  );
}
